const API_BASE_URL = process.env.REACT_APP_API_URL || '/api/v1';

class ApiService {
  constructor() {
    this.baseUrl = API_BASE_URL;
  }

  getHeaders() {
    const headers = {
      'Content-Type': 'application/json',
    };

    const token = localStorage.getItem('token');
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    return headers;
  }

  buildQueryString(params) {
    const filtered = {};
    Object.keys(params).forEach((key) => {
      if (params[key] !== '' && params[key] !== null && params[key] !== undefined) {
        filtered[key] = params[key];
      }
    });
    const query = new URLSearchParams(filtered).toString();
    return query ? `?${query}` : '';
  }

  async request(endpoint, options = {}) {
    const url = `${this.baseUrl}${endpoint}`;
    const config = {
      ...options,
      headers: {
        ...this.getHeaders(),
        ...options.headers,
      },
    };

    try {
      const response = await fetch(url, config);

      if (response.status === 401) {
        localStorage.removeItem('token');
        window.location.href = '/login';
        throw new Error('Unauthorized');
      }

      if (response.status === 403) {
        let forbiddenMessage = 'Доступ запрещён';
        try {
          const error = await response.json();
          if (typeof error.detail === 'string') {
            forbiddenMessage = error.detail;
          }
        } catch {
          // ignore parse errors
        }
        throw new Error(forbiddenMessage);
      }

      if (!response.ok) {
        let errorMessage = 'Ошибка запроса к API';
        try {
          const error = await response.json();
          console.error('Server error response:', error);
          // Если detail - массив (validation errors), форматируем его
          if (Array.isArray(error.detail)) {
            errorMessage = error.detail.map(e => `${e.loc?.join('.')}: ${e.msg}`).join(', ');
          } else if (typeof error.detail === 'object' && error.detail !== null) {
            errorMessage = JSON.stringify(error.detail);
          } else {
            errorMessage = error.detail || error.message || errorMessage;
          }
        } catch (jsonError) {
          // Если ответ не JSON, используем текст ответа
          const textError = await response.text();
          console.error('Server error text:', textError);
          errorMessage = textError ? `Ошибка сервера: ${textError.substring(0, 100)}` : 'Ошибка запроса к API';
        }
        throw new Error(errorMessage);
      }

      // Если статус 204 No Content, возвращаем null вместо парсинга JSON
      if (response.status === 204) {
        return null;
      }

      return await response.json();
    } catch (error) {
      console.error('API Error:', error);
      throw error;
    }
  }

  async login(email, password) {
    // Пробуем сначала как админ
    let response = await fetch(`${this.baseUrl}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });

    // Если не админ — пробуем как сотрудник
    if (!response.ok) {
      response = await fetch(`${this.baseUrl}/auth/employee-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
    }

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Ошибка входа. Проверьте логин и пароль');
    }

    const data = await response.json();
    localStorage.setItem('token', data.access_token);
    return data;
  }

  async forgotPassword(email) {
    const response = await fetch(`${this.baseUrl}/auth/password/forgot?email=${encodeURIComponent(email)}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Ошибка при отправке запроса');
    }

    return await response.json();
  }

  async resetPassword(token, newPassword) {
    const response = await fetch(`${this.baseUrl}/auth/password/reset`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ token, new_password: newPassword }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Ошибка при сбросе пароля');
    }

    return await response.json();
  }

  async getCurrentUser() {
    return this.request('/auth/me');
  }

  async getUsers(params = {}) {
    const query = this.buildQueryString(params);
    return this.request(`/users/${query}`);
  }

  async getUser(id) {
    return this.request(`/users/${id}`);
  }

  async updateUser(id, data) {
    return this.request(`/users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteUser(id) {
    return this.request(`/users/${id}`, { method: 'DELETE' });
  }

  async getApplications(params = {}) {
    const query = this.buildQueryString(params);
    return this.request(`/applications/${query}`);
  }

  async getApplication(id) {
    return this.request(`/applications/${id}`);
  }

  async updateApplication(id, data) {
    return this.request(`/applications/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async assignManager(applicationId, managerId) {
    return this.request(`/applications/${applicationId}/assign`, {
      method: 'POST',
      body: JSON.stringify({ manager_id: managerId }),
    });
  }

  async getEmployees(params = {}) {
    const query = this.buildQueryString(params);
    return this.request(`/employees/${query}`);
  }

  async getEmployee(id) {
    return this.request(`/employees/${id}`);
  }

  async createEmployee(data) {
    return this.request('/employees/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateEmployee(id, data) {
    return this.request(`/employees/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteEmployee(id) {
    return this.request(`/employees/${id}`, {
      method: 'DELETE',
    });
  }

  async getMessages(params = {}) {
    const query = this.buildQueryString(params);
    return this.request(`/content/messages/${query}`);
  }

  async getMessage(id) {
    return this.request(`/content/messages/${id}`);
  }

  async createMessage(data) {
    return this.request('/content/messages/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateMessage(id, data) {
    return this.request(`/content/messages/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteMessage(id) {
    return this.request(`/content/messages/${id}`, {
      method: 'DELETE',
    });
  }

  // Message Versions
  async getMessageVersions(messageId) {
    return this.request(`/content/messages/${messageId}/versions/`);
  }

  async createMessageVersion(messageId, data) {
    return this.request(`/content/messages/${messageId}/versions/`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateMessageVersion(messageId, versionId, data) {
    return this.request(`/content/messages/${messageId}/versions/${versionId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async activateMessageVersion(messageId, versionId) {
    return this.request(`/content/messages/${messageId}/versions/${versionId}/activate`, {
      method: 'POST',
    });
  }

  async deleteMessageVersion(messageId, versionId) {
    return this.request(`/content/messages/${messageId}/versions/${versionId}`, {
      method: 'DELETE',
    });
  }

  async getQAItems(params = {}) {
    return [
      { id: 1, category: 'Общие', question: 'Как начать работу?', answer: 'Начните с регистрации' },
      { id: 2, category: 'Общие', question: 'Как связаться?', answer: 'Напишите в поддержку' },
    ];
  }

  async updateQAItem(id, data) {
    console.log('Update QA item:', id, data);
    return { success: true };
  }

  async getFeedback(params = {}) {
    const query = this.buildQueryString(params);
    return this.request(`/feedback${query}`);
  }

  async updateFeedback(id, data) {
    return this.request(`/feedback/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async getManagers(params = {}) {
    const query = this.buildQueryString(params);
    return this.request(`/employees/${query}`);
  }

  async getSchedules() {
    return this.request('/employees/schedules/');
  }

  async createSchedule(data) {
    return this.request('/employees/schedules/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateSchedule(id, data) {
    return this.request(`/employees/schedules/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteSchedule(id) {
    return this.request(`/employees/schedules/${id}`, {
      method: 'DELETE',
    });
  }

  async getDirections() {
    return this.request('/employees/directions/');
  }

  async createDirection(data) {
    return this.request('/employees/directions/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateDirection(id, data) {
    return this.request(`/employees/directions/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteDirection(id) {
    return this.request(`/employees/directions/${id}`, {
      method: 'DELETE',
    });
  }

  async createManager(data) {
    return this.createEmployee(data);
  }

  async updateManager(id, data) {
    return this.updateEmployee(id, data);
  }

  async deleteManager(id) {
    return this.deleteEmployee(id);
  }

  // Dialogs / Communications
  async getDialogsList(params = {}) {
    const query = this.buildQueryString(params);
    return this.request(`/dialogs/list${query}`);
  }

  async getCommunicationsData() {
    return this.request('/dialogs/communications');
  }

  async getCommunicationsTabs(options = {}) {
    return this.request('/dialogs/communications/tabs', options);
  }

  async getGroupCombinedMessages(channelChattypeId, botUccId = null, limit = 200, options = {}) {
    let url = `/dialogs/communications/tabs/group-messages/${channelChattypeId}?limit=${limit}`;
    if (botUccId) {
      url += `&bot_ucc_id=${botUccId}`;
    }
    return this.request(url, options);
  }

  async getDialogHistory(dialogId, limit = 100, options = {}) {
    return this.request(`/dialogs/history/${dialogId}?limit=${limit}`, options);
  }

  // Prompts
  async getPrompts() {
    return this.request('/prompts/');
  }

  async getPrompt(id) {
    return this.request(`/prompts/${id}`);
  }

  async updatePrompt(id, data) {
    return this.request(`/prompts/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  // Prompt Versions
  async getPromptVersions(promptId) {
    return this.request(`/prompts/${promptId}/versions/`);
  }

  async createPromptVersion(promptId, data) {
    return this.request(`/prompts/${promptId}/versions/`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updatePromptVersion(promptId, versionId, data) {
    return this.request(`/prompts/${promptId}/versions/${versionId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async activatePromptVersion(promptId, versionId) {
    return this.request(`/prompts/${promptId}/versions/${versionId}/activate`, {
      method: 'POST',
    });
  }

  async deletePromptVersion(promptId, versionId) {
    return this.request(`/prompts/${promptId}/versions/${versionId}`, {
      method: 'DELETE',
    });
  }

  // Classes
  async getClasses(params = {}) {
    const query = this.buildQueryString(params);
    return this.request(`/classes/${query}`);
  }

  async getClass(id) {
    return this.request(`/classes/${id}`);
  }

  async createClass(data) {
    return this.request('/classes/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateClass(id, data) {
    return this.request(`/classes/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteClass(id) {
    return this.request(`/classes/${id}`, {
      method: 'DELETE',
    });
  }

  async createPrompt(classId, data) {
    return this.request(`/classes/${classId}/prompts/`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async deletePrompt(classId, promptId) {
    await this.request(`/classes/${classId}/prompts/${promptId}`, {
      method: 'DELETE'
    });
  }

  async setActivePrompt(classId, promptId) {
    return this.request(`/classes/${classId}/prompts/${promptId}/activate`, {
      method: 'POST'
    });
  }

  async updateClassPrompt(classId, promptId, data) {
    return this.request(`/classes/${classId}/prompts/${promptId}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  async getClassPrompts(classId) {
    return this.request(`/classes/${classId}/prompts/`);
  }

  // Generic GET method
  async get(endpoint) {
    return this.request(endpoint, {
      method: 'GET',
    });
  }

  // Dialogs API - Users with dialogs
  async getUsersWithDialogs(skip = 0, limit = 100) {
    return this.get(`/dialogs/users-with-dialogs?skip=${skip}&limit=${limit}`);
  }

  // Dialogs API - Chats for user
  async getUserChats(userId, skip = 0, limit = 100) {
    return this.get(`/dialogs/history/users/${userId}/chats?skip=${skip}&limit=${limit}`);
  }

  // Dialogs API - Messages in chat
  async getChatMessages(chatId, skip = 0, limit = 50) {
    return this.get(`/dialogs/history/chats/${chatId}/messages?skip=${skip}&limit=${limit}`);
  }

  // Company Info Examples API methods
  async getCompanyInfoExamples(channelId = null, isActive = null, skip = 0, limit = 100) {
    let params = new URLSearchParams({
      skip: skip.toString(),
      limit: limit.toString()
    });

    if (channelId !== null) {
      params.append('channel_id', channelId.toString());
    }

    if (isActive !== null) {
      params.append('is_active', isActive.toString());
    }

    return this.get(`/company-info-examples/?${params.toString()}`);
  }

  async getCompanyInfoExample(exampleId) {
    return this.get(`/company-info-examples/${exampleId}`);
  }

  async createCompanyInfoExample(data) {
    return this.request('/company-info-examples/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateCompanyInfoExample(exampleId, data) {
    return this.request(`/company-info-examples/${exampleId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteCompanyInfoExample(exampleId) {
    return this.request(`/company-info-examples/${exampleId}`, {
      method: 'DELETE',
    });
  }

  // Mailings API methods


  async getMailings(params = {}) {
    const query = this.buildQueryString(params);
    return this.request(`/mailings/${query}`);
  }

  async getMailing(id) {
    return this.request(`/mailings/${id}`);
  }
  async getMailingStats() {
    return this.request(`/mailings/stats`);
  }

  async getMailingRecipientsCount(targetClasses) {
    return this.request('/mailings/recipients-count', {
      method: 'POST',
      body: JSON.stringify({ target_classes: targetClasses || [] }),
    });
  }

  async createMailing(data) {
    return this.request('/mailings/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateMailing(id, data) {
    return this.request(`/mailings/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async delayedStartCommitMailing(id) {
    return this.request(`/mailings/${id}/delayed_start_commit`, {
      method: 'POST',
    });
  }

  async deleteMailing(id) {
    return this.request(`/mailings/${id}`, {
      method: 'DELETE',
    });
  }

  async uploadMailingImage(id, formData) {
    const url = `${this.baseUrl}/mailings/${id}/image`;

    // Берём токен так же, как в getHeaders
    const token = localStorage.getItem('token');

    const headers = {};
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    // ВАЖНО: не ставим Content-Type вручную, браузер сам добавит boundary

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: formData,
    });

    if (response.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
      throw new Error('Unauthorized');
    }

    if (!response.ok) {
      let errorMessage = 'Ошибка загрузки изображения';
      try {
        const error = await response.json();
        errorMessage = error.detail || errorMessage;
      } catch (jsonError) {
        const textError = await response.text();
        errorMessage = textError ? `Ошибка сервера: ${textError.substring(0, 100)}` : errorMessage;
      }
      throw new Error(errorMessage);
    }

    return await response.json();
  }
  delayedStartMailing(id) {
  return this.request(`/mailings/${id}/delayed_start`, {
    method: 'POST',
  });
  }

  async sendTestMailing(data) {
    return this.request('/mailings/single-user', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  cancelStartMailing(id) {
  return this.request(`/mailings/${id}/cancel_start`, {
    method: 'POST',
  });
  }


}

const api = new ApiService();
export default api;